
import React from "react";
import { Control } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WaitlistFormValues } from "../../schema";
import { useCounties } from "@/hooks/use-counties";

interface CountySelectorProps {
  control: Control<WaitlistFormValues>;
  selectedCountySlugs: string[];
  setSelectedCountySlugs: (slugs: string[]) => void;
}

export function CountySelector({ 
  control, 
  selectedCountySlugs, 
  setSelectedCountySlugs 
}: CountySelectorProps) {
  const { data: counties, isLoading: countiesLoading } = useCounties();

  return (
    <FormField
      control={control}
      name="preferredLocation"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Preferred Counties <span className="text-red-500">*</span></FormLabel>
          <DropdownMenu>
            <FormControl>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-start bg-white border border-gray-300 hover:bg-gray-50"
                >
                  {field.value && field.value.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {field.value.map(slug => {
                        const county = counties?.find(c => c.slug === slug);
                        return (
                          <Badge key={slug} variant="secondary" className="mr-1 mb-1">
                            {county?.name || slug}
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Select counties</span>
                  )}
                </Button>
              </DropdownMenuTrigger>
            </FormControl>
            <DropdownMenuContent className="w-full max-w-[400px] max-h-[300px] overflow-y-auto bg-white">
              <ScrollArea className="h-[300px]">
                {counties?.map((county) => (
                  <DropdownMenuCheckboxItem
                    key={county.slug}
                    checked={field.value?.includes(county.slug)}
                    onCheckedChange={(checked) => {
                      const updatedValue = checked
                        ? [...field.value || [], county.slug]
                        : field.value?.filter(slug => slug !== county.slug) || [];
                      
                      field.onChange(updatedValue);
                      setSelectedCountySlugs(updatedValue);
                    }}
                  >
                    {county.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
